import React, { useState, useEffect } from 'react';
import { createProgramme, updateProgramme } from '../api/programmes';
import { Programme, ProgrammeBase, ProgrammeCreate } from '../api/programmes';
import Button from './Button';

interface ProgrammeFormProps {
  programme?: Programme;
  /** Create and update return the programme without its rolled-up metrics. */
  onSuccess?: (programme: ProgrammeBase) => void;
  onCancel?: () => void;
}

const ProgrammeForm: React.FC<ProgrammeFormProps> = ({ programme, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    start_date: '',
    end_date: '',
    budget: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isEditMode = !!programme;

  useEffect(() => {
    if (programme) {
      setFormData({
        name: programme.name || '',
        description: programme.description || '',
        start_date: programme.start_date || '',
        end_date: programme.end_date || '',
        budget: programme.budget?.toString() || '',
      });
    }
  }, [programme]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const programmeData: ProgrammeCreate = {
        name: formData.name,
        description: formData.description || undefined,
        start_date: formData.start_date || undefined,
        end_date: formData.end_date || undefined,
        budget: formData.budget ? parseFloat(formData.budget) : undefined,
      };

      let updatedProgramme: ProgrammeBase;
      if (isEditMode && programme) {
        updatedProgramme = await updateProgramme(programme.id, programmeData);
      } else {
        updatedProgramme = await createProgramme(programmeData);
      }

      setFormData({
        name: '',
        description: '',
        start_date: '',
        end_date: '',
        budget: '',
      });

      if (onSuccess) {
        onSuccess(updatedProgramme);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : `Failed to ${isEditMode ? 'update' : 'create'} programme`;
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="card">
      <h3 style={{ marginTop: 0, marginBottom: 'var(--spacing-lg)', color: 'var(--gray-900)' }}>
        {isEditMode ? 'Edit Programme' : 'Create New Programme'}
      </h3>

      {error && <div className="p-4 bg-red-50 dark:bg-red-900/20 border-2 border-red-500 dark:border-red-700 rounded-lg text-red-900 dark:text-red-100">{error}</div>}

      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Name <span style={{ color: 'var(--error)' }}>*</span>
        </label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          className="w-full p-2.5 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md transition-all focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
          required
          maxLength={200}
          placeholder="Enter programme name"
        />
      </div>

      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Description</label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          className="w-full p-2.5 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md transition-all focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 resize-vertical min-h-[100px]"
          rows={4}
          maxLength={2000}
          placeholder="Enter programme description (optional)"
        />
      </div>

      <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-md)' }}>
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Start Date</label>
          <input
            type="date"
            name="start_date"
            value={formData.start_date}
            onChange={handleChange}
            className="w-full p-2.5 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md transition-all focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
          />
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">End Date</label>
          <input
            type="date"
            name="end_date"
            value={formData.end_date}
            onChange={handleChange}
            className="w-full p-2.5 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md transition-all focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
          />
        </div>
      </div>

      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Budget</label>
        <input
          type="number"
          name="budget"
          value={formData.budget}
          onChange={handleChange}
          className="w-full p-2.5 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md transition-all focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
          min="0"
          step="0.01"
          placeholder="Enter budget (optional)"
        />
      </div>

      <div style={{ display: 'flex', gap: 'var(--spacing-md)', marginTop: 'var(--spacing-lg)' }}>
        <Button type="submit" disabled={loading} variant="primary">
          {loading ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Programme' : 'Create Programme')}
        </Button>
        {onCancel && (
          <Button type="button" onClick={onCancel} variant="outline">
            Cancel
          </Button>
        )}
      </div>
    </form>
  );
};

export default ProgrammeForm;
